import React from "react";

const InteractiveTechChart = () => {
  const nodes = {
    center: {
      x: 500,
      y: 300,
      label: "🚀 GeniusNexa",
      color: "#0f1724",
      width: 180,
      height: 60,
    },
    webDev: {
      x: 300,
      y: 180,
      label: "💻 Web Development",
      color: "#3b82f6",
      width: 160,
      height: 48,
    },
    mobileDev: {
      x: 700,
      y: 180,
      label: "📱 Mobile Apps",
      color: "#10b981",
      width: 140,
      height: 48,
    },
    cloudSolutions: {
      x: 300,
      y: 420,
      label: "☁️ Cloud Solutions",
      color: "#8b5cf6",
      width: 160,
      height: 48,
    },
    aiSolutions: {
      x: 700,
      y: 420,
      label: "🤖 AI Solutions",
      color: "#f59e0b",
      width: 140,
      height: 48,
    },
  };

  const subNodes = {
    // Web Development Services
    reactDev: {
      x: 180,
      y: 100,
      label: "⚛️ React.js",
      color: "#61dafb",
      width: 120,
      height: 36,
    },
    nextjs: {
      x: 300,
      y: 60,
      label: "▲ Next.js",
      color: "#1a1a1a",
      width: 120,
      height: 36,
    },
    nodejs: {
      x: 420,
      y: 100,
      label: "💚 Node.js",
      color: "#339933",
      width: 120,
      height: 36,
    },

    // Mobile App Services
    reactNative: {
      x: 580,
      y: 100,
      label: "📲 React Native",
      color: "#61dafb",
      width: 140,
      height: 36,
    },
    flutter: {
      x: 700,
      y: 60,
      label: "🐦 Flutter",
      color: "#02569b",
      width: 120,
      height: 36,
    },
    ios: {
      x: 820,
      y: 100,
      label: "🍎 iOS Native",
      color: "#007aff",
      width: 120,
      height: 36,
    },

    // Cloud Solutions
    aws: {
      x: 180,
      y: 500,
      label: "☁️ AWS",
      color: "#ff9900",
      width: 100,
      height: 36,
    },
    azure: {
      x: 300,
      y: 540,
      label: "☁️ Azure",
      color: "#0078d4",
      width: 100,
      height: 36,
    },
    docker: {
      x: 420,
      y: 500,
      label: "🐳 Docker",
      color: "#2496ed",
      width: 100,
      height: 36,
    },

    // AI Solutions
    chatbots: {
      x: 580,
      y: 500,
      label: "💬 Chatbots",
      color: "#ff6b6b",
      width: 120,
      height: 36,
    },
    automation: {
      x: 700,
      y: 540,
      label: "⚙️ Automation",
      color: "#4ecdc4",
      width: 120,
      height: 36,
    },
    dataAnalytics: {
      x: 820,
      y: 500,
      label: "📊 Analytics",
      color: "#45b7d1",
      width: 120,
      height: 36,
    },
  };

  const connections = [
    // Main service connections
    ["center", "webDev"],
    ["center", "mobileDev"],
    ["center", "cloudSolutions"],
    ["center", "aiSolutions"],

    // Web Development technologies
    ["webDev", "reactDev"],
    ["webDev", "nextjs"],
    ["webDev", "nodejs"],

    // Mobile App technologies
    ["mobileDev", "reactNative"],
    ["mobileDev", "flutter"],
    ["mobileDev", "ios"],

    // Cloud Solutions
    ["cloudSolutions", "aws"],
    ["cloudSolutions", "azure"],
    ["cloudSolutions", "docker"],

    // AI Solutions
    ["aiSolutions", "chatbots"],
    ["aiSolutions", "automation"],
    ["aiSolutions", "dataAnalytics"],
  ];

  const nodeFor = (key) => nodes[key] || subNodes[key];

  const renderArrow = (fromKey, toKey, idx) => {
    const from = nodeFor(fromKey);
    const to = nodeFor(toKey);
    if (!from || !to) return null;
    return (
      <line
        key={idx}
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke="#334155"
        strokeWidth={2.5}
        markerEnd="url(#arrowhead-normal)"
        opacity={0.95}
        strokeLinecap="round"
      />
    );
  };

  const renderBox = (node, key) => {
    const lines = node.label.split("\\n");
    const w = node.width || 120;
    const h = node.height || 36;
    const lightTextFills = [
      "#0f1724",
      "#0ea5a4",
      "#0891b2",
      "#1a1a1a",
      "#000000",
    ];
    const textFill = lightTextFills.includes(node.color.toLowerCase())
      ? "#ffffff"
      : "#06121a";

    const rectRx = node.rx ?? (key === "prescriptive" ? 18 : 12);

    return (
      <g key={key} className="node-group">
        <rect
          x={node.x - w / 2}
          y={node.y - h / 2}
          width={w}
          height={h}
          rx={rectRx}
          ry={rectRx}
          fill={node.color}
          className="transition-all duration-300 hover:scale-110 hover:drop-shadow-lg cursor-pointer"
          style={{
            transformOrigin: `${node.x}px ${node.y}px`,
          }}
        />
        <text
          x={node.x}
          y={node.y}
          textAnchor="middle"
          fill={textFill}
          fontSize={14}
          fontWeight={700}
          dominantBaseline="middle"
          className="pointer-events-none transition-all duration-300"
        >
          {lines.map((line, i) => (
            <tspan
              key={i}
              x={node.x}
              dy={i === 0 ? -((lines.length - 1) * 8) : 16}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  return (
    <div className="interactive-tech-chart w-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[#0d1625] dark:to-[#121e2e] rounded-2xl p-4 md:p-6 shadow-lg border border-gray-200 dark:border-white/10">
      <style jsx>{`
        .node-group:hover rect {
          filter: drop-shadow(0 10px 25px rgba(0, 0, 0, 0.25));
        }
        .node-group:hover {
          z-index: 10;
        }
      `}</style>
      <svg
        preserveAspectRatio="xMidYMid meet"
        viewBox="0 0 1000 650"
        className="w-full h-auto"
        style={{ maxHeight: "740px" }}
      >
        <defs>
          <marker
            id="arrowhead-normal"
            markerWidth="16"
            markerHeight="16"
            refX="12"
            refY="8"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path d="M0,0 L12,8 L0,16 Z" fill="#334155" />
          </marker>
        </defs>

        {connections.map((c, i) => renderArrow(c[0], c[1], i))}
        {Object.entries(subNodes).map(([k, n]) => renderBox(n, k))}
        {Object.entries(nodes).map(([k, n]) => renderBox(n, k))}
      </svg>
    </div>
  );
};

export default InteractiveTechChart;
